/**
 * @file ShopNavGroup.tsx
 * @description Сворачиваемая группа меню кабинета магазина (phase19.31,
 * SC14, SC18; макет панели — uiux-design §3.1, вариант Б).
 *
 * Отличие от макета §3.1 (там стрелка ▾/▸ на месте значка и пункты без
 * значков) — по замечанию владельца 2026-10-02 «исчезли иконки»:
 *  - заголовок группы — строка того же шрифта, что пункт (14/500, цвет
 *    пунктов панели): значок группы слева (в одной вертикали со значками
 *    «Сводки» и «Настроек»), название, сумма счётчиков свёрнутой группы и
 *    стрелка раскрытия СПРАВА; высота 48 до 1024 и 36 с 1024
 *    (`data-slot="sidebar-group"` в console-rules.css);
 *  - вложенные пункты — со своими значками чуть меньше (18px) и правее
 *    значков групп, чтобы вложенность читалась; выбранный — подложка (и
 *    полоса слева у светлых тем); длинные подписи переносятся;
 *  - подзаголовки внутри группы («Доставка») — из поля `section` пунктов.
 * Какая группа открыта — решает родитель (аккордеон, `useNavAccordion`).
 */

import { ChevronDown } from "lucide-react";
import { Fragment } from "react";

import type { ShopNavGroup as ShopNavGroupData, ShopNavItem } from "../../lib/shop-nav-registry";
import { groupCounterSum, itemHref, type NavCounters } from "../../lib/shop-nav";
import { SidebarNavLink } from "../ui/NavLink";
import { cn } from "@/lib/utils";

interface Props {
  group: ShopNavGroupData;
  slug: string;
  counters: NavCounters;
  open: boolean;
  onToggle: (groupId: string) => void;
}

export function ShopNavGroup({ group, slug, counters, open, onToggle }: Props) {
  const sum = groupCounterSum(group, counters);
  const panelId = `nav-group-${group.id}`;
  const Icon = group.icon;

  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        data-slot="sidebar-group"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={(e) => {
          // Клик по заголовку не закрывает шторку меню на телефоне.
          e.stopPropagation();
          onToggle(group.id);
        }}
        className={cn(
          "flex w-full items-center gap-3 rounded-md px-3 py-1 text-left font-medium",
          "text-sidebar-foreground transition-colors hover:bg-sidebar-hover hover:text-sidebar-hover-foreground",
          "outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        )}
      >
        <Icon aria-hidden className="size-5 shrink-0" />
        <span className="min-w-0 flex-1 break-words">{group.label}</span>
        {!open && sum > 0 ? (
          <span
            className="min-w-6 shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-center text-xs font-semibold leading-tight text-on-primary tabular-nums"
            aria-label={`ждут действия: ${sum}`}
          >
            {sum}
          </span>
        ) : null}
        <ChevronDown
          aria-hidden
          data-nav-chevron=""
          className={cn("size-4 shrink-0 text-sidebar-muted transition-transform duration-200", open ? "rotate-0" : "-rotate-90")}
        />
      </button>
      {open ? (
        <div id={panelId} className="flex flex-col gap-0.5">
          {group.items.map((item, index) => (
            <Fragment key={item.id}>
              {item.section && item.section !== group.items[index - 1]?.section ? (
                <div className="pb-1 pl-9 pr-3 pt-2 text-xs text-sidebar-muted">{item.section}</div>
              ) : null}
              <NavItemLink item={item} slug={slug} counters={counters} inset />
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Пункт меню из реестра: ссылка, значок, счётчик. */
export function NavItemLink({
  item,
  slug,
  counters,
  forceActive,
  inset = false,
}: {
  item: ShopNavItem;
  slug: string;
  counters: NavCounters;
  forceActive?: boolean;
  /** Вложенный пункт группы — значок меньше и правее. */
  inset?: boolean;
}) {
  const Icon = item.icon;
  return (
    <SidebarNavLink
      to={itemHref(slug, item)}
      icon={<Icon />}
      label={item.label}
      end={item.end}
      excludePaths={item.excludePaths?.map((p) => `/shop/${slug}/${p}`)}
      badge={item.counter ? counters[item.counter] : undefined}
      forceActive={forceActive}
      inset={inset}
    />
  );
}
